import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Search,
  Phone,
  User,
  Calendar,
  Ticket,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Payment method display
const getPaymentMethodDisplay = (method) => {
  return method === 'modempay' ? 'ModemPay' : method?.charAt(0).toUpperCase() + method?.slice(1) || 'Unknown';
};

export default function PaymentsPage() {
  const { getAuthHeader } = useAuth();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [filter]);

  const fetchPayments = async () => {
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(
        `${API_URL}/api/admin/payments${statusParam}`,
        getAuthHeader()
      );
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/payments/stats`,
        getAuthHeader()
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleConfirmPayment = async (paymentId, action) => {
    setProcessingId(paymentId);
    try {
      await axios.post(
        `${API_URL}/api/admin/payments/confirm`,
        { payment_id: paymentId, action },
        getAuthHeader()
      );
      toast.success(action === 'confirm' ? 'Payment confirmed! Tickets generated.' : 'Payment rejected');
      fetchPayments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} payment`);
    } finally {
      setProcessingId(null);
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      payment.payment_id.toLowerCase().includes(search) ||
      payment.user_name.toLowerCase().includes(search) ||
      payment.user_email.toLowerCase().includes(search) ||
      payment.event_name.toLowerCase().includes(search) ||
      (payment.reference_number && payment.reference_number.toLowerCase().includes(search))
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" /> Confirmed</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPaymentMethodBadge = (method) => {
    const logo = PAYMENT_LOGOS[method];
    if (method === 'wave') {
      return (
        <Badge className="bg-[#1DA1F2]/10 text-[#1DA1F2] hover:bg-[#1DA1F2]/10 flex items-center gap-1">
          <img src={logo} alt="Wave" className="w-4 h-4 rounded-full" />
          Wave
        </Badge>
      );
    }
    return (
      <Badge className="bg-[#8B5CF6]/10 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 flex items-center gap-1">
        <img src={logo} alt="Yonna" className="w-4 h-4 rounded-full" />
        Yonna
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Payments</h1>
            <p className="text-muted-foreground">Manage and confirm customer payments</p>
          </div>
          <Button onClick={() => { fetchPayments(); fetchStats(); }} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-2xl font-bold">{stats.pending}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Confirmed</p>
                    <p className="text-2xl font-bold">{stats.confirmed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Rejected</p>
                    <p className="text-2xl font-bold">{stats.rejected}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Revenue</p>
                    <p className="text-2xl font-bold">D{stats.total_confirmed_amount?.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by ID, name, email, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'confirmed', 'rejected'].map((status) => (
              <Button
                key={status}
                variant={filter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(status)}
                className={filter === status ? 'bg-[#0055FF]' : ''}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Payments List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPayments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No payments found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredPayments.map((payment) => (
              <Card key={payment.id} className={payment.status === 'pending' ? 'border-yellow-200 bg-yellow-50/30' : ''}>
                <CardContent className="pt-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Payment Info */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono font-bold text-lg">{payment.payment_id}</span>
                        {getStatusBadge(payment.status)}
                        {getPaymentMethodBadge(payment.payment_method)}
                        <Badge variant="outline">
                          {payment.payment_type === 'push' ? 'Push Payment' : 'Manual Transfer'}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span>{payment.user_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Ticket className="w-4 h-4 text-muted-foreground" />
                          <span>{payment.event_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>{formatDate(payment.created_at)}</span>
                        </div>
                        {payment.phone_number && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span>{payment.phone_number}</span>
                          </div>
                        )}
                      </div>

                      {payment.reference_number && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">Reference:</span>
                          <span className="font-mono font-medium bg-gray-100 px-2 py-1 rounded">
                            {payment.reference_number}
                          </span>
                        </div>
                      )}

                      {payment.tickets && payment.tickets.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap text-sm">
                          <span className="text-muted-foreground">Tickets:</span>
                          {payment.tickets.map((ticketId) => (
                            <Badge key={ticketId} variant="outline" className="font-mono">
                              {ticketId}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Amount & Actions */}
                    <div className="flex flex-col items-end gap-3">
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Amount</p>
                        <p className="font-heading text-2xl font-bold">D{payment.amount}</p>
                        <p className="text-xs text-muted-foreground">{payment.quantity} ticket(s)</p>
                      </div>

                      {payment.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleConfirmPayment(payment.payment_id, 'confirm')}
                            disabled={processingId === payment.payment_id}
                          >
                            {processingId === payment.payment_id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Confirm
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleConfirmPayment(payment.payment_id, 'reject')}
                            disabled={processingId === payment.payment_id}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Help Text */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How to confirm payments:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Check your Wave/Yonna account for incoming payments</li>
                  <li>Match the reference number with the payment record</li>
                  <li>Verify the amount matches</li>
                  <li>Click "Confirm" to generate tickets for the customer</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
